import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import LeaseApiService from '../../services/apiService';
import { rewriteLocalhostImageUrl, fetchAuthenticatedImageBlob, isUnitImageFile } from './unitImageHelpers';

const IMAGE_URL = process.env.REACT_APP_IMAGE_URL;

const ImagesList = ({ attachments, setDeletedFiles, deletedFiles, setAttachments, createEmptyRow, readOnly = false }) => {
    const { t } = useTranslation();
    const apiService = new LeaseApiService();

    // Attachment
    const [attachmentModal, setAttachmentModal] = useState(false);
    // Preview renders inline in a panel under the grid rather than a popup
    // modal - a modal read as "nothing visible happened" behind whatever was
    // rendering it. { url, isBlob, loading, error } - url points at a local
    // blob: url once resolved (either the freshly-picked File itself, or the
    // saved image fetched with auth headers - see handleView) so the <img>
    // never has to load the raw backend url directly.
    const [previewImage, setPreviewImage] = useState(null);
    const previewRequestId = useRef(0);
    // Attachment Functions
    const handleFileSelect = useCallback((file, id) => {
        setAttachments((prev) => {
            const updated = prev.map((row) =>
                row.id === id
                    ? {
                        ...row,
                        file,
                        fileName: file.name,
                        isNew: true,   // ✅ mark new
                    }
                    : row
            );

            return [...updated, createEmptyRow()];
        });
    }, []);
    const handleRemarkChange = (id, value) => {
        setAttachments((prev) =>
            prev.map((row) =>
                row.id === id ? { ...row, remarks: value } : row
            )
        );
    };
    const handleDelete = async (row) => {
        if (!row.isNew && row.filePath) {
            setDeletedFiles((prev) => [...prev, row.fileName]);

            const fileparams = new URLSearchParams();
            fileparams.append("filePath", row.filePath);

            await apiService.deleteParameter(
                "DELETE_FILE",
                `?${fileparams.toString()}`
            );
        }

        setAttachments((prev) => {
            // If only one row exists, clear it instead of removing it
            if (prev.length === 1) {
                return [
                    {
                        
                        ...prev[0],
                        file: null,
                        fileName: "",
                        remarks: "",
                        filePath: "",
                        fullUrl: "",
                        isNew: true,
                    },
                ];
            }

            // Otherwise remove the row normally
            return prev.filter((item) => item.id !== row.id);
        });
    };
    const handleView = async (row) => {
        if (!row) return;

        if (previewImage?.isBlob && previewImage?.url) {
            URL.revokeObjectURL(previewImage.url);
        }

        // A freshly-picked, not-yet-uploaded file is already a local File -
        // no network involved, nothing that can 401 or point at a bad host.
        if (row.isNew) {
            const isImage = isUnitImageFile(row);
            setPreviewImage({ url: URL.createObjectURL(row.file), isImage, isBlob: true, loading: false, error: false, rawUrl: '' });
            return;
        }

        // A saved image's fullUrl is a plain <img src> away from a 401 (no
        // auth headers on a bare browser request) or a bad host baked in at
        // upload time - fetch it as an authenticated blob instead.
        const rawUrl = rewriteLocalhostImageUrl(row.fullUrl);
        const requestId = ++previewRequestId.current;
        const isImage = isUnitImageFile(row, rawUrl);
        setPreviewImage({ url: null, isImage, isBlob: false, loading: true, error: false, rawUrl });
        try {
            const blob = await fetchAuthenticatedImageBlob(rawUrl);
            if (previewRequestId.current !== requestId) return;
            // A PDF served without a proper filename/extension still comes back
            // with an accurate blob.type - trust that over the extension guess.
            setPreviewImage({ url: URL.createObjectURL(blob), isImage: isImage || blob.type?.startsWith('image/'), isBlob: true, loading: false, error: false, rawUrl });
        } catch (error) {
            console.error('Error fetching image preview:', error);
            if (previewRequestId.current !== requestId) return;
            setPreviewImage({ url: null, isImage, isBlob: false, loading: false, error: true, rawUrl });
        }
    };
    const closePreview = () => {
        if (previewImage?.isBlob && previewImage?.url) {
            URL.revokeObjectURL(previewImage.url);
        }
        setPreviewImage(null);
    };

    useEffect(() => {
        return () => {
            if (previewImage?.isBlob && previewImage?.url) {
                URL.revokeObjectURL(previewImage.url);
            }
        };
    }, [previewImage]);

    return (
        <div className=''>
            <table className="table table-bordered">
                <thead className="dynoTable">
                    <tr>
                        <th>{t('common.fileName')}</th>
                        <th>{t('common.remarks')}</th>
                        {!readOnly && <th>{t('common.upload')}</th>}
                        <th>{t('common.view')}</th>
                        {!readOnly && <th>{t('common.delete')}</th>}
                    </tr>
                </thead>
                <tbody>
                    {attachments.map((row) => (
                        <tr key={row.id}>
                            <td>
                                <input disabled value={row.fileName} className="form-control" />
                            </td>

                            <td>
                                <input
                                    disabled={readOnly}
                                    value={row.remarks}
                                    className="form-control"
                                    onChange={(e) =>
                                        handleRemarkChange(row.id, e.target.value)
                                    }
                                />
                            </td>

                            {!readOnly && (
                            <td>
                                <input
                                    type="file"
                                    hidden
                                    id={`file-${row.id}`}
                                    onChange={(e) =>
                                        handleFileSelect(e.target.files[0], row.id)
                                    }
                                />
                                <button
                                    className="mainbtn"
                                    onClick={() =>
                                        document.getElementById(`file-${row.id}`).click()
                                    }
                                >
                                    {t('common.upload')}
                                </button>
                            </td>
                            )}

                            <td>
                                <button
                                    className="mainbtnRed"
                                    disabled={!row.fileName}
                                    onClick={() => handleView(row)}
                                >
                                    {t('common.view')}
                                </button>
                            </td>

                            {!readOnly && (
                            <td>
                                <button
                                    className="mainbtnSecondary"
                                    onClick={() => handleDelete(row)}
                                >
                                    {t('common.delete')}
                                </button>
                            </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>

            {previewImage && (
                <div className="mt-3 p-3" style={{ border: '1px solid #E5E7EB', borderRadius: '8px', background: '#FAFAFA' }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>{t('common.preview')}</strong>
                        <FaTimes onClick={closePreview} style={{ cursor: 'pointer' }} title={t('common.close')} />
                    </div>
                    {previewImage.loading && <div className="text-center p-4">{t('common.loading')}</div>}
                    {previewImage.error && (
                        <div className="text-center p-4">
                            <div className="mb-2">{t('common.previewFailed')}</div>
                            {previewImage.rawUrl && (
                                <a href={previewImage.rawUrl} target="_blank" rel="noopener noreferrer">
                                    {t('common.openInNewTab')}
                                </a>
                            )}
                        </div>
                    )}
                    {previewImage.url && !previewImage.loading && !previewImage.error && (
                        previewImage.isImage ? (
                            <img
                                src={previewImage.url}
                                alt="preview"
                                style={{ maxWidth: '100%', maxHeight: '600px', display: 'block', margin: '0 auto' }}
                            />
                        ) : (
                            <iframe
                                src={previewImage.url}
                                title="preview"
                                style={{ width: '100%', height: '600px', border: 'none' }}
                            />
                        )
                    )}
                </div>
            )}
        </div>
    )
}

export default ImagesList